import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger } from '@mono/ui-components';
import clsx from 'clsx';
import { memo, useState } from 'react';
import { ComplianceStatus, ObligationStrength, RequirementCategory } from '../../../types/proposal';
const STATUS_OPTIONS = [
    {
        value: ComplianceStatus.ADDRESSED,
        label: 'Addressed'
    },
    {
        value: ComplianceStatus.PARTIAL,
        label: 'Partial'
    },
    {
        value: ComplianceStatus.NOT_STARTED,
        label: 'Not Started'
    },
    {
        value: ComplianceStatus.IN_PROGRESS,
        label: 'In Progress'
    },
    {
        value: ComplianceStatus.NON_COMPLIANT,
        label: 'Non-Compliant'
    },
    {
        value: ComplianceStatus.NOT_APPLICABLE,
        label: 'N/A'
    }
];
const CATEGORY_OPTIONS = [
    {
        value: RequirementCategory.PROPOSAL_INSTRUCTION,
        label: 'Proposal Instruction'
    },
    {
        value: RequirementCategory.FORMAT_REQUIREMENT,
        label: 'Format Requirement'
    },
    {
        value: RequirementCategory.SUBMISSION_REQUIREMENT,
        label: 'Submission Requirement'
    },
    {
        value: RequirementCategory.SCOPE_REQUIREMENT,
        label: 'Scope Requirement'
    },
    {
        value: RequirementCategory.PERFORMANCE_STANDARD,
        label: 'Performance Standard'
    },
    {
        value: RequirementCategory.DELIVERABLE,
        label: 'Deliverable'
    },
    {
        value: RequirementCategory.TASK_REQUIREMENT,
        label: 'Task Requirement'
    },
    {
        value: RequirementCategory.EVALUATION_CRITERION,
        label: 'Evaluation Criterion'
    },
    {
        value: RequirementCategory.QUALIFICATION,
        label: 'Qualification'
    },
    {
        value: RequirementCategory.COMPLIANCE_REQUIREMENT,
        label: 'Compliance Requirement'
    },
    {
        value: RequirementCategory.CLAUSE_FLOWDOWN,
        label: 'Clause Flowdown'
    },
    {
        value: RequirementCategory.SECURITY_REQUIREMENT,
        label: 'Security Requirement'
    },
    {
        value: RequirementCategory.KEY_PERSONNEL,
        label: 'Key Personnel'
    },
    {
        value: RequirementCategory.REPORTING_REQUIREMENT,
        label: 'Reporting Requirement'
    },
    {
        value: RequirementCategory.PERIOD_OF_PERFORMANCE,
        label: 'Period of Performance'
    },
    {
        value: RequirementCategory.PLACE_OF_PERFORMANCE,
        label: 'Place of Performance'
    },
    {
        value: RequirementCategory.CLIN,
        label: 'CLIN'
    },
    {
        value: RequirementCategory.OTHER,
        label: 'Other'
    }
];
const OBLIGATION_OPTIONS = [
    {
        value: ObligationStrength.SHALL,
        label: 'SHALL (Mandatory)'
    },
    {
        value: ObligationStrength.MUST,
        label: 'MUST (Mandatory)'
    },
    {
        value: ObligationStrength.WILL,
        label: 'WILL (Mandatory)'
    },
    {
        value: ObligationStrength.SHOULD,
        label: 'SHOULD (Desirable)'
    },
    {
        value: ObligationStrength.MAY,
        label: 'MAY (Optional)'
    }
];
const RESPONSE_ACTION_OPTIONS = [
    {
        value: 'WRITE_APPROACH',
        label: 'Write Approach'
    },
    {
        value: 'PROVIDE_EVIDENCE',
        label: 'Provide Evidence'
    },
    {
        value: 'COMPLY_FORMAT',
        label: 'Comply Format'
    },
    {
        value: 'SUBMIT_ACTION',
        label: 'Submit Action'
    },
    {
        value: 'ACKNOWLEDGE',
        label: 'Acknowledge'
    },
    {
        value: 'PRICE',
        label: 'Price'
    },
    {
        value: 'STAFF',
        label: 'Staff'
    }
];
const DELIVERABLE_TYPE_OPTIONS = [
    {
        value: 'PROPOSAL',
        label: 'Proposal'
    },
    {
        value: 'CONTRACT',
        label: 'Contract'
    },
    {
        value: 'BOTH',
        label: 'Both'
    }
];
const ACTION_OWNER_OPTIONS = [
    {
        value: 'PROPOSAL_ACTION',
        label: 'Proposal Team'
    },
    {
        value: 'CONTRACT_ACTION',
        label: 'Contractor Performance'
    },
    {
        value: 'GOVERNMENT_ACTION',
        label: 'Government'
    },
    {
        value: 'NO_ACTION',
        label: 'No Action'
    }
];
export const Filters = /*#__PURE__*/ memo(function Filters({ filters, updateFilters, onClear }) {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const handleSearchChange = (e)=>{
        updateFilters({
            searchQuery: e.target.value
        });
    };
    const handleStatusToggle = (status)=>{
        const newStatuses = filters.statuses.includes(status) ? filters.statuses.filter((s)=>s !== status) : [
            ...filters.statuses,
            status
        ];
        updateFilters({
            statuses: newStatuses
        });
    };
    const handleCategoryToggle = (category)=>{
        const newCategories = filters.categories.includes(category) ? filters.categories.filter((c)=>c !== category) : [
            ...filters.categories,
            category
        ];
        updateFilters({
            categories: newCategories
        });
    };
    const handleObligationToggle = (obligation)=>{
        const newObligations = filters.obligations.includes(obligation) ? filters.obligations.filter((o)=>o !== obligation) : [
            ...filters.obligations,
            obligation
        ];
        updateFilters({
            obligations: newObligations
        });
    };
    const handleResponseActionToggle = (action)=>{
        const newActions = filters.responseActions.includes(action) ? filters.responseActions.filter((a)=>a !== action) : [
            ...filters.responseActions,
            action
        ];
        updateFilters({
            responseActions: newActions
        });
    };
    const handleDeliverableTypeToggle = (type)=>{
        const newTypes = filters.deliverableTypes.includes(type) ? filters.deliverableTypes.filter((t)=>t !== type) : [
            ...filters.deliverableTypes,
            type
        ];
        updateFilters({
            deliverableTypes: newTypes
        });
    };
    const handleActionOwnerToggle = (owner)=>{
        const newOwners = filters.actionOwners.includes(owner) ? filters.actionOwners.filter((o)=>o !== owner) : [
            ...filters.actionOwners,
            owner
        ];
        updateFilters({
            actionOwners: newOwners
        });
    };
    const hasActiveFilters = filters.searchQuery || filters.statuses.length > 0 || filters.categories.length > 0 || filters.obligations.length > 0 || filters.responseActions.length > 0 || filters.deliverableTypes.length > 0 || filters.actionOwners.length > 0;
    const activeFilterCount = filters.statuses.length + filters.categories.length + filters.obligations.length + filters.responseActions.length + filters.deliverableTypes.length + filters.actionOwners.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white rounded-lg border border-gray-200 p-4 space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>setIsCollapsed(!isCollapsed),
                                type: "button",
                                className: "p-1 hover:bg-gray-100 rounded transition-colors",
                                "aria-label": isCollapsed ? 'Expand filters' : 'Collapse filters',
                                children: /*#__PURE__*/ _jsxs("svg", {
                                    className: clsx('w-5 h-5 text-gray-700 transition-transform', {
                                        'rotate-180': isCollapsed
                                    }),
                                    fill: "none",
                                    stroke: "currentColor",
                                    viewBox: "0 0 24 24",
                                    role: "img",
                                    "aria-label": isCollapsed ? 'Expand filters' : 'Collapse filters',
                                    children: [
                                        /*#__PURE__*/ _jsx("title", {
                                            children: isCollapsed ? 'Expand filters' : 'Collapse filters'
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            strokeLinecap: "round",
                                            strokeLinejoin: "round",
                                            strokeWidth: 2,
                                            d: "M19 14l-7 7m0 0l-7-7m7 7V3"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-sm font-medium text-gray-900",
                                children: "Filters"
                            }),
                            hasActiveFilters && /*#__PURE__*/ _jsx("span", {
                                className: "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700",
                                children: activeFilterCount
                            })
                        ]
                    }),
                    hasActiveFilters && /*#__PURE__*/ _jsx("button", {
                        onClick: onClear,
                        type: "button",
                        className: "text-sm text-blue-600 hover:text-blue-700 font-medium",
                        children: "Clear All"
                    })
                ]
            }),
            !isCollapsed && /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-3 items-center flex-wrap",
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: filters.searchQuery,
                        onChange: handleSearchChange,
                        placeholder: "Search requirements...",
                        className: clsx('flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-md', 'text-sm placeholder-gray-400', 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500')
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Status ",
                                        filters.statuses.length > 0 && `(${filters.statuses.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: STATUS_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.statuses.includes(option.value),
                                                    onChange: ()=>handleStatusToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Category ",
                                        filters.categories.length > 0 && `(${filters.categories.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: CATEGORY_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.categories.includes(option.value),
                                                    onChange: ()=>handleCategoryToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Obligation ",
                                        filters.obligations.length > 0 && `(${filters.obligations.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: OBLIGATION_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.obligations.includes(option.value),
                                                    onChange: ()=>handleObligationToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Action ",
                                        filters.responseActions.length > 0 && `(${filters.responseActions.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: RESPONSE_ACTION_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.responseActions.includes(option.value),
                                                    onChange: ()=>handleResponseActionToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Deliverable",
                                        ' ',
                                        filters.deliverableTypes.length > 0 && `(${filters.deliverableTypes.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: DELIVERABLE_TYPE_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.deliverableTypes.includes(option.value),
                                                    onChange: ()=>handleDeliverableTypeToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Popover, {
                        children: [
                            /*#__PURE__*/ _jsx(PopoverTrigger, {
                                asChild: true,
                                children: /*#__PURE__*/ _jsxs("button", {
                                    type: "button",
                                    className: "px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500",
                                    children: [
                                        "Owner ",
                                        filters.actionOwners.length > 0 && `(${filters.actionOwners.length})`
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(PopoverContent, {
                                className: "w-56 p-0",
                                align: "start",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-2 max-h-60 overflow-y-auto",
                                    children: ACTION_OWNER_OPTIONS.map((option)=>/*#__PURE__*/ _jsxs("label", {
                                            className: "flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: filters.actionOwners.includes(option.value),
                                                    onChange: ()=>handleActionOwnerToggle(option.value),
                                                    className: "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-2 text-sm text-gray-700",
                                                    children: option.label
                                                })
                                            ]
                                        }, option.value))
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
